import { PrintHeader } from '@/components/print/print-header'

interface PrintEmptySheetProps {
  kitchenName: string
  rangeDateLabel: string
  /** What the packet would have been — "Ingredients", "Recipes", ... */
  title: string
  /** Why there is nothing to print, from `printAvailability`. */
  reason: string
}

/**
 * The print document for a range that has no packet.
 *
 * The app shell wears `print-screen-only` (globals.css), so it is hidden from
 * every print unconditionally and the `.print-only` portal is the ONLY thing
 * that can render. When the range has no placed shipments that portal used to
 * mount nothing at all — and a system print (Chrome's menu, or the Android
 * share sheet) then produced a genuinely blank page, with no hint as to why.
 *
 * That is indistinguishable from the Android blank-print bug fixed in
 * #2088/#2103, and it is what sent a chef chasing a print failure that was
 * really an empty range (2026-08-31). Mounting this sheet instead keeps the
 * ambient-print invariant honest: there is ALWAYS a print document, so a
 * print always yields a readable page.
 */
export function PrintEmptySheet({ kitchenName, rangeDateLabel, title, reason }: PrintEmptySheetProps) {
  return (
    <div className="font-sans text-black">
      <PrintHeader kitchenName={kitchenName} dateLabel={rangeDateLabel} />
      <header className="mt-12 border-b-2 border-black pb-4">
        <p className="text-xs uppercase tracking-wide">Print packet</p>
        <h1 className="mt-2 text-4xl font-semibold leading-tight">{kitchenName}</h1>
        <p className="mt-2 text-lg">{rangeDateLabel}</p>
      </header>
      <section className="mt-10">
        <h2 className="text-2xl font-semibold">Nothing to print</h2>
        <p className="mt-3 text-lg">{reason}.</p>
        <p className="mt-6 text-base">
          This {title.toLowerCase()} packet covers placed orders only. Once orders are placed for this range, printing
          it again will produce the full packet.
        </p>
      </section>
    </div>
  )
}
